/**
 * Candles, drawn by TradingView Lightweight Charts. Task 31.
 *
 * `Tooling-and-Libraries.md` is in bold about this: **"Do not build charting."** The library
 * owns the axes, the crosshair, the zoom and the hit-testing. This file owns four things it
 * cannot know about, and each is a rule from somewhere else in the project.
 *
 * ## 1. A hole in the series is not drawn as continuous
 *
 * Lightweight Charts packs the bars it is given side by side, so a series missing six hours
 * renders as two adjacent candles six hours apart with nothing to say so. That is the exact
 * failure `data_gaps` exists to prevent — F12 was 536 minutes of missing bars that every
 * reader saw as a complete range — reproduced in pixels.
 *
 * The fix is **whitespace points**: an entry carrying a time and no prices, which the library
 * renders as a gap in the plot and a tick on the axis. So the series handed to it is not the
 * bars, it is the bars *plus every interval between them that has no bar*.
 *
 * ## 2. Synthetic bars are marked
 *
 * `bars.synthetic` is true for a manufactured placeholder for an interval the venue omitted —
 * final and unobserved at the same time. Drawing one identically to an observed candle is
 * drawing a price nobody traded, so each gets a marker.
 *
 * ## 3. Colour comes from the tokens, never from the library's defaults
 *
 * The design system forbids a colour that is not in `tokens/`, and Lightweight Charts ships
 * its own green and red. They are read out of the computed styles at mount and re-read when
 * the theme changes, because the tokens differ between dark and light.
 *
 * ## 4. Nothing animates
 *
 * `ChartFrame`'s own contract: *"No chart animates."* The library's price-line animation and
 * its default transitions are turned off rather than left at their defaults.
 *
 * ## 5. A live bar is an `update()`, never a `setData()` (task 33)
 *
 * `setData()` replaces the series: it re-allocates every point, discards the operator's zoom
 * and pan, and re-runs `fitContent`. Doing that once a minute means a chart nobody can hold
 * still long enough to read — and on a 1m series with 500 points it is 500 objects rebuilt to
 * change one. `update()` amends the last candle in place, or appends when the timestamp is a
 * new interval, which is exactly the two cases a `bar.closed` push carries.
 *
 * **It has one precondition and the library throws when it is violated**: the time handed to
 * `update()` must be at or after the series' last point. A push can arrive for a bar older
 * than the last one drawn — the REST fetch and the socket are two sources with no ordering
 * between them, and a reconnect refetches while pushes are already flowing — so the timestamp
 * is compared before the call. Dropping an old push is right: the series already holds that
 * interval, from the endpoint that is authoritative about history.
 */

import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  createChart,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type TickMarkType,
  type Time,
} from 'lightweight-charts';
import { useEffect, useRef } from 'react';

import { useTimestampMode } from '../ui/timestamps';
import { axisTickLabel, crosshairTimeLabel } from './axisTime';

import type { BarResponse } from '../api/generated';
import { toPlot } from '../money';
import { secondsOf, withGaps } from './gaps';

export interface CandleChartProps {
  bars: BarResponse[];
  /** Seconds per bar, so the missing intervals can be worked out. */
  intervalSeconds: number;
  height?: number;
  /** Re-read the tokens when this changes. */
  theme: 'dark' | 'light';
  /**
   * The newest bar to arrive over `/ws`, or `null`. Applied with `update()` — see rule 5.
   *
   * A prop rather than an imperative handle the parent calls. The chart then has one way in
   * for data and React owns the ordering, so a push that arrives during the same commit as a
   * new `bars` array cannot be applied to the series the old array built.
   */
  liveBar?: BarResponse | null;
}

function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function CandleChart({
  bars,
  intervalSeconds,
  height = 420,
  theme,
  liveBar = null,
}: CandleChartProps): React.JSX.Element {
  // ⚠ **A ref, read inside the formatters.** The library keeps the callbacks it was given at
  // creation, so a captured `mode` would freeze at whatever it was when the chart mounted and the
  // axis would stop following the toggle. The ref lets the same callback read the current value.
  const timestampMode = useTimestampMode();
  const modeRef = useRef(timestampMode);
  modeRef.current = timestampMode;
  const holder = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<'Candlestick'> | null>(null);
  /**
   * Seconds of the newest point the series holds, tracked here rather than read back from the
   * library — `ISeriesApi` has no "last point" accessor, and `dataByIndex` would mean a lookup
   * per push to answer a question this already knows.
   */
  const newest = useRef<number | null>(null);

  useEffect(() => {
    const element = holder.current;
    if (!element) return;

    const created = createChart(element, {
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: token('--text-tertiary'),
        fontFamily: token('--font-mono') || 'monospace',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: token('--border-subtle') },
        horzLines: { color: token('--border-subtle') },
      },
      crosshair: {
        // Magnet snaps to the nearest bar, which is what an operator reading a price wants;
        // free-floating invites reading a value between two candles.
        mode: CrosshairMode.Magnet,
      },
      rightPriceScale: { borderColor: token('--border-default') },
      timeScale: {
        borderColor: token('--border-default'),
        // 🔴 **The comment that used to be here named this defect and did not fix it.** It said
        // the library "would otherwise label the axis in the browser's zone … two clocks on one
        // screen is how a trade log gets misread" — above `timeVisible` and `secondsVisible`,
        // **neither of which controls the timezone.** `tickMarkFormatter` defaults to `undefined`,
        // so the built-in formatter ran and the axis rendered in a zone nobody chose, underneath a
        // `ChartFrame` caption asserting `times in UTC`.
        //
        // Now it follows the same switch as every other timestamp (`UI-Design-Spec.md` line 120),
        // and — the part that matters independently of which mode is active — the answer is
        // *defined* rather than inherited.
        tickMarkFormatter: (time: Time, tickMarkType: TickMarkType) =>
          axisTickLabel(time, tickMarkType, modeRef.current),
        timeVisible: true,
        secondsVisible: false,
      },
      localization: {
        // The crosshair has to agree with the axis, or the chart contradicts itself under the
        // cursor — which is worse than either label alone being wrong.
        timeFormatter: (time: Time) => crosshairTimeLabel(time, modeRef.current),
      },
      handleScale: { axisPressedMouseMove: { time: true, price: false } },
    });

    const candles = created.addSeries(CandlestickSeries, {
      upColor: token('--profit'),
      downColor: token('--loss'),
      borderUpColor: token('--profit'),
      borderDownColor: token('--loss'),
      wickUpColor: token('--profit'),
      wickDownColor: token('--loss'),
      // Rule 4. The library animates the last price by default.
      lastValueVisible: true,
      priceLineVisible: false,
    });

    chart.current = created;
    series.current = candles;

    const resize = () => created.applyOptions({ width: element.clientWidth });
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);

    return () => {
      observer.disconnect();
      created.remove();
      chart.current = null;
      series.current = null;
    };
    // `theme` is a dependency because the tokens are read at construction: a theme flip has
    // to rebuild the chart, and rebuilding is cheap next to tracking every colour option.
  }, [height, theme]);

  useEffect(() => {
    const candles = series.current;
    if (!candles) return;
    // `withGaps` lives in its own module with no charting import, so the arithmetic that
    // stops a hole being drawn as continuous is unit-tested rather than eyeballed on a
    // canvas — see `gaps.test.ts`. The cast is the one place the two type worlds meet.
    candles.setData(withGaps(bars, intervalSeconds, toPlot) as Parameters<typeof candles.setData>[0]);
    chart.current?.timeScale().fitContent();
    // Whatever the fetch just drew is now the floor for `update()`. Reset here rather than in
    // the live effect: a timeframe change replaces the series wholesale, and a stale floor from
    // the previous rung would reject every push on the new one — a chart that silently stops
    // updating after the operator touches the dropdown.
    const last = bars[bars.length - 1];
    newest.current = last ? secondsOf(last.ts) : null;

    // Rule 2 — one marker per manufactured bar, but 🔴 **only one label per contiguous run.**
    //
    // Every synthetic bar carried the word `synthetic` and adjacent bars are a few pixels apart,
    // so a run of three drew three labels on top of each other and rendered as
    // `syynntthheettiicc`. It read as a font or encoding fault; it was three correct labels in
    // the same place. Found by screenshotting the chart on 2026-08-05.
    //
    // ⚠ The fix labels the run rather than dropping markers: every manufactured bar keeps its
    // dot, because the dot is the claim *this bar was not observed* and it is made per bar. The
    // label names the run, and a run of more than one says how many — which the old rendering
    // could not tell you even when it was legible.
    const markers = bars
      .map((bar, index) => ({ bar, index }))
      .filter(({ bar }) => bar.synthetic)
      .map(({ bar, index }) => {
        const startsRun = !(bars[index - 1]?.synthetic ?? false);
        let length = 0;
        if (startsRun) {
          while (bars[index + length]?.synthetic ?? false) length += 1;
        }
        return {
          time: secondsOf(bar.ts) as Time,
          position: 'belowBar' as const,
          // 🔴 **`--data-ink`, not `--attn`.** Amber means real money is at risk and appears nowhere
        // else — that is world law and the operator's own confirmed decision. A manufactured bar is
        // a data-integrity annotation on a screen with no capital on it, so amber here was a false
        // risk signal on every paper chart. The circle's shape and the legend's words carry the
        // meaning; the colour was never doing the work.
        color: token('--data-ink'),
          shape: 'circle' as const,
          text: startsRun ? (length > 1 ? `${length} synthetic` : 'synthetic') : '',
        };
      });
    createSeriesMarkers(candles, markers);
  }, [bars, intervalSeconds]);

  // Rule 5. One candle, in place.
  useEffect(() => {
    const candles = series.current;
    if (!candles || !liveBar) return;

    const at = secondsOf(liveBar.ts);
    // The precondition `update()` throws on. See rule 5 for why an out-of-order push is real
    // rather than defensive, and why dropping it is the right answer.
    if (newest.current !== null && at < newest.current) return;

    candles.update({
      time: at as Time,
      open: toPlot(liveBar.open),
      high: toPlot(liveBar.high),
      low: toPlot(liveBar.low),
      close: toPlot(liveBar.close),
    });
    newest.current = at;
    // No `fitContent()` and no `setData()`. The operator's zoom and pan survive a live update,
    // which is what makes a moving chart readable — and `--dur-chart` is `0ms`, so the new
    // candle appears on the frame it arrived rather than easing into place.
  }, [liveBar]);

  return <div ref={holder} style={{ width: '100%' }} />;
}
